/**
 * Block 12 calculator — `countertopQ`: количество столешниц.
 *
 * Применимо только к **Кухне** и **Тумбе-ванна** (per методике §2.1 Б.6 +
 * §2.3 Б.6). Возвращает либо **штук листов** (ЛДСП / HPL), либо **погонные
 * метры** (Камень акрил / Камень кварц / Литой камень) — клиент определяет
 * единицу по `countertopUnit(kind)`.
 *
 * Reference: `docs/methodology/calculator-formulas.md` §2.1 Б.6 + §2.3 Б.6 +
 *            §4.9 cheat-sheet + TZ §11.4 disambiguation.
 */

import { assertPositiveDimension, type BathTabletopParams, type KitchenParams } from './types';

/**
 * Типы столешницы (общие для Кухни и Тумбы-ванна):
 * - `'laminated'` — Столешницы ЛДСП (sheets, user выбирает 3 или 4 м)
 * - `'hpl'` — Столешницы HPL (sheets, фикс 4м — ADR-0033)
 * - `'acrylic-stone'` — Камень акрил (lm)
 * - `'quartz-stone'` — Камень кварц (lm)
 * - `'cast-stone'` — Литой камень (lm)
 */
export type CountertopKind = 'laminated' | 'hpl' | 'acrylic-stone' | 'quartz-stone' | 'cast-stone';

/** Единица измерения qty для countertop. */
export type CountertopUnit = 'sheets' | 'lm';

/**
 * Возвращает единицу измерения для заданного типа столешницы.
 *
 * - ЛДСП/HPL → `'sheets'` (штук листов, integer)
 * - Камень любой → `'lm'` (погонные метры, decimal)
 */
export function countertopUnit(kind: CountertopKind): CountertopUnit {
  if (kind === 'laminated' || kind === 'hpl') return 'sheets';
  return 'lm';
}

/**
 * Опции для `countertopQKitchen`. `lvlsSheetLength` применяется только если
 * `kind='laminated'` (для HPL фикс 4м per ADR-0033, для камня — pm).
 */
export interface KitchenCountertopOptions {
  kind: CountertopKind;
  /**
   * Длина стандартного листа ЛДСП в мм. По умолчанию 3000.
   * Только для `kind='laminated'` — другие kinds игнорируют (HPL фикс 4000).
   *
   * Per OQ-3 ответ Aibek: user может выбрать 3000 или 4000.
   */
  lvlsSheetLength?: 3000 | 4000;
}

/**
 * Опции для `countertopQBathTabletop`.
 *
 * PR-A1 (ADR-0033) — `lvlsSheetLength` теперь применяется для `kind='laminated'`
 * (user выбирает 3 или 4 м, mirror Кухни). Раньше Тумба-ванна игнорировала длину
 * листа (фикс 3000), хотя UI/схема `countertopSheetLen` его собирали — dead UI bug.
 * HPL фикс 4000 (не выбирается), камень — pm.
 */
export interface BathTabletopCountertopOptions {
  kind: CountertopKind;
  /**
   * Длина стандартного листа ЛДСП в мм. По умолчанию 3000.
   * Только для `kind='laminated'` — HPL фикс 4000, камень игнорирует.
   */
  lvlsSheetLength?: 3000 | 4000;
}

/**
 * Кухня countertopQ.
 *
 * **Камень акрил / Камень кварц / Литой камень (pm):**
 * - прямая: `L / 1000`
 * - угловая: `(L + L2) / 1000` — **без −600 для камня** (per §4.9 + TZ §11.4)
 *
 * **Столешницы ЛДСП (листов):**
 * - прямая: `Math.ceil(L / sheetLen)`
 * - угловая: `Math.ceil((L + L2 − 600) / sheetLen)` — **с −600** (per §4.9)
 * - `sheetLen ∈ {3000, 4000}` (user picks), default 3000.
 *
 * **Столешницы HPL (листов) — фикс 4м (ADR-0033):**
 * - прямая: `Math.ceil(L / 4000)`
 * - угловая: `Math.ceil((L + L2 − 600) / 4000)`
 * - длина листа НЕ выбирается пользователем (фикс 4000), в отличие от ЛДСП.
 *
 * ⚠️ **Asymmetry note:** Камень угловой НЕ применяет −600 (per §4.9 + TZ).
 * Это расходится с §2.1 Б.6 narrative «totalL для всех kinds» — TZ §11.4
 * disambiguation: −600 только для листовых материалов (ЛДСП/HPL). См. B-097.
 *
 * **Источник:** xlsx `Кухня!C38-I38` + §4.9 + §2.1 Б.6 + OQ-3.
 *
 * Reference: `docs/methodology/calculator-formulas.md` §2.1 Б.6 + §4.9.
 *
 * @throws Error если `L = 0`, `isAngular=true` без `length2`, или
 *   `L+L2 ≤ 600` (для угловой ЛДСП/HPL).
 *
 * @example
 *   Кухня прямая L=4500, kind=Камень: 4.5 пм
 *   Кухня угловая L=3000, L2=2000, kind=Камень: (3000+2000)/1000 = 5.0 пм
 *   Кухня прямая L=5000, kind=ЛДСП, sheetLen=3000: ceil(5000/3000) = 2 листа
 *   Кухня угловая L=3000, L2=2000, kind=ЛДСП, sheetLen=3000:
 *     ceil((3000+2000-600)/3000) = ceil(1.467) = 2 листа
 *   Кухня прямая L=3500, kind=HPL: ceil(3500/4000) = 1 лист (фикс 4м, ADR-0033)
 */
export function countertopQKitchen(params: KitchenParams, opts: KitchenCountertopOptions): number {
  assertPositiveDimension(params.length, 'countertopQKitchen.length');

  const isStone =
    opts.kind === 'acrylic-stone' || opts.kind === 'quartz-stone' || opts.kind === 'cast-stone';

  // Камень: pm (decimal). Угловая без −600 (per §4.9).
  if (isStone) {
    if (params.isAngular) {
      assertPositiveDimension(params.length2, 'countertopQKitchen.length2 (isAngular=true)');
      return (params.length + params.length2) / 1000;
    }
    return params.length / 1000;
  }

  // ЛДСП / HPL: листов (integer). Угловая С −600.
  // PR-A1 (ADR-0033): ЛДСП — user выбирает 3000/4000 (default 3000); HPL — фикс
  // 4000 (Aibek «но только 4 м, длина одного листа 4 м», не user choice).
  const sheetLen = opts.kind === 'laminated' ? (opts.lvlsSheetLength ?? 3000) : 4000;

  let totalL: number;
  if (params.isAngular) {
    assertPositiveDimension(params.length2, 'countertopQKitchen.length2 (isAngular=true)');
    totalL = params.length + params.length2 - 600;
    if (totalL <= 0) {
      throw new Error(
        `countertopQKitchen: angular L+L2 must be > 600mm for sheet kinds, got L=${params.length}, L2=${params.length2}`,
      );
    }
  } else {
    totalL = params.length;
  }

  return Math.ceil(totalL / sheetLen);
}

/**
 * Тумба-ванна countertopQ.
 *
 * **Камень (любой): pm (decimal).**
 * - `L / 1000`
 *
 * **ЛДСП: листов (user выбирает 3 или 4 м — ADR-0033).**
 * - `Math.ceil(L / sheetLen)`, `sheetLen ∈ {3000, 4000}`, default 3000.
 *
 * **HPL: листов (фикс 4м — ADR-0033).**
 * - `Math.ceil(L / 4000)`
 *
 * PR-A1 (ADR-0033): раньше ЛДСП/HPL оба считались по фикс 3000, хотя UI/схема
 * `countertopSheetLen` собирали выбор для ЛДСП (dead UI bug). Теперь ЛДСП
 * уважает выбор, HPL — фикс 4000 (mirror Кухни).
 *
 * **Источник:** xlsx `Тумба-ванна!D34` + Aibek visual review 2026-05-29.
 *
 * Reference: `docs/methodology/calculator-formulas.md` §2.3 Б.6 + §4.9 + ADR-0033.
 *
 * @throws Error если `L = 0`.
 *
 * @example
 *   L=1500, kind=Камень: 1.5 пм
 *   L=1500, kind=ЛДСП, sheetLen=3000: ceil(1500/3000) = 1 лист
 *   L=3500, kind=ЛДСП, sheetLen=4000: ceil(3500/4000) = 1 лист
 *   L=3500, kind=HPL: ceil(3500/4000) = 1 лист (фикс 4м)
 */
export function countertopQBathTabletop(
  params: BathTabletopParams,
  opts: BathTabletopCountertopOptions,
): number {
  assertPositiveDimension(params.length, 'countertopQBathTabletop.length');

  const isStone =
    opts.kind === 'acrylic-stone' || opts.kind === 'quartz-stone' || opts.kind === 'cast-stone';

  if (isStone) {
    return params.length / 1000;
  }

  // PR-A1 (ADR-0033): ЛДСП — user выбирает 3000/4000 (default 3000); HPL — фикс 4000.
  const sheetLen = opts.kind === 'laminated' ? (opts.lvlsSheetLength ?? 3000) : 4000;
  return Math.ceil(params.length / sheetLen);
}
